import { CAT_TRADEMARKS } from '@/app/utils/Collections';
import { CATEGORY_PLURAL_TO_SINGLE } from '@/data/category-map';
import { isAuthTokenValid, decodeJWT } from '@/lib/Utils';
// import { env } from '../../project.config';
// import { ROLE_DIGITAL_HEALTHCARE, ROLE_STAFF } from 'App/constants/Roles';
import { ROLE_STAFF } from 'App/constants/Roles';

let INSTANTIATED = false;
const EVENTS = new Map();
const RBAC = new Set();
// The backend caps a bookmarks response at 200 items whatever `itemsPerPage` asks for,
// so a full list has to be collected page by page.
const BOOKMARKS_PAGE_SIZE = 200;

function initUI() {
  return {
    bookmarks: [],
    filters: [],
    reports: [],
    perks: [],
  };
}

// noinspection EqualityComparisonWithCoercionJS
class UserModel {
  #bookmarksRequest = null;
  #emit = (event, ...args) => {
    const handlers = EVENTS.get(event);
    if (handlers?.length) {
      for (const handler of handlers) {
        if (typeof handler === 'function') {
          handler(...args);
        }
      }
    }
  };
  #setRoles = (roles) => {
    RBAC.clear();
    roles.forEach(({ name }) => RBAC.add(name.toLowerCase()));
  };
  #reset = () => {
    this.id = null;
    this.username = '';
    this.logged = false;
    this.token = null;
    this.profile = {};
    this.#bookmarksRequest = null;
    this.ui = {
      bookmarks: [],
      filters: [],
      reports: [],
      perks: [],
    };
    RBAC.clear();
  };

  id = null;
  username = '';
  logged = false;
  token = null;
  refresh_token = null;
  profile = {};
  ui = {};

  constructor(Dataman) {
    if (INSTANTIATED) {
      throw new Error('User instance already exists');
    }
    INSTANTIATED = true;
    this.DM = Dataman;
    this.ui = initUI();
  }

  setUI = (json) => {
    try {
      const ui = JSON.parse(json) || {};
      this.ui.perks = ui.perks || [];
    } catch (e) {
      console.error(e);
    }

    return this.ui;
  };
  loadBookmarks = async (params = { itemsPerPage: 200, page: 1 }) => {
    const bookmarks = await this.DM.getBookmarks(params);
    return { items: bookmarks.data['hydra:member'], total: bookmarks.data['hydra:totalItems'] };
  };
  loadFilters = async () => {
    const filters = await this.DM.getFilters({ itemsPerPage: 200, page: 1 });
    return filters.data || [];
  };
  loadProfile = async () => {
    const [user, , filters] = await Promise.all([this.DM.getUser(), this.bookmarks.ensure(), this.loadFilters()]);
    const profile = user.data;
    this.#setRoles(profile.groups);
    this.setUI(profile.ui);
    this.profile = profile;
    this.#emit('profile', this);

    this.ui.filters = filters;

    return profile;
  };

  updateProfile = async (fields) => {
    const data = {};
    if (!fields?.length) {
      fields = ['name', 'phone', 'company'];
    }
    fields.forEach((field) => {
      data[field] = this.profile[field];
    });
    return this.DM.updProfile(this.id, data);
  };
  getToken = async (credentials) => {
    const response = await this.DM.getToken(credentials);
    //const { token } = response?.data || {};
    const { token, refresh_token } = response?.data || {};

    if (!token) {
      throw new Error('Can not get auth token');
    }
    return { token, refresh_token };
  };
  auth = async ({ token, refresh_token }) => {
    const { payload } = decodeJWT(token);
    this.token = token;
    this.refresh_token = refresh_token;
    this.id = payload.id;
    this.username = payload.username;
    payload.roles.forEach((role) => RBAC.add(role.toLowerCase()));
    this.logged = true;
    this.DM.setLocalToken({ token, refresh_token });
    this.DM.setToken({ token, refresh_token });
    await this.loadProfile();
    this.#emit('login', this);
    return payload.id;
  };
  login = async (credentials) => this.auth(await this.getToken(credentials));
  logout = async () => {
    this.DM.clearLocalToken();
    this.#reset();
    this.#emit('logout');
  };
  checkAuthToken = async () => {
    const { token, refresh_token } = this.DM.getLocalToken();
    if (token && isAuthTokenValid(token)) {
      return this.auth({ token, refresh_token });
    } else if (refresh_token) {
      try {
        const newTokens = await this.DM.refreshToken(refresh_token);
        return this.auth(newTokens);
      } catch (err) {
        this.DM.clearLocalToken();
        this.#reset();
        return false;
      }
    } else {
      this.DM.clearLocalToken();
      this.#reset();
      return undefined;
    }
  };
  getLastLoginTime = () => {
    const { token } = this.DM.getLocalToken();
    if (!token) {
      return null;
    }
    const { payload } = decodeJWT(token);
    return payload.iat;
  };
  updateUI = () => {
    this.profile.ui = JSON.stringify({
      ...this.ui,
      filters: this.ui.filters?.map((item) => JSON.stringify(item)) || null,
    });
    return this.updateProfile(['ui']);
  };
  clearUI = () => {
    this.profile.ui = JSON.stringify({});
    this.ui = initUI();
    return this.updateProfile(['ui']);
  };
  reportsCount = () => this.ui.reports.length;
  Dev = () => false;
  Help = () => false;
  Perk = (perk, attach = true) => {
    const index = this.ui.perks.indexOf(perk);
    if (!!attach && index < 0) {
      this.ui.perks.push(perk);
    }
    if (!attach && index > -1) {
      this.ui.perks.splice(index, 1);
    }
    return this.updateUI();
  };
  filters = {
    add: async (filter) => {
      const { data } = await this.DM.createFilters(filter);
      if (this.ui.filters === null) {
        this.ui.filters = [];
      }
      this.ui.filters.push(data);
      return data;
    },
    update: async (filter) => {
      const { data: updatedFilter } = await this.DM.updateFilter(filter.id, filter);
      this.ui.filters = this.ui.filters.map((oldFilter) =>
        oldFilter.id === updatedFilter.id ? updatedFilter : oldFilter
      );
    },
    remove: async (item) => {
      await this.DM.deleteFilter(item.id);
      this.ui.filters = this.ui.filters.filter(({ id }) => id !== item.id);
    },
    list: (module) => {
      return this.ui.filters.filter((el) => !module || el.module === module);
    },
    amount: () => this.ui.filters.length,
    shortlist: (module, len = 7) => {
      let filters = this.ui.filters;
      const usedLabels = [];
      filters = filters.filter((filter) => {
        if (usedLabels.includes(filter.label.trim())) {
          return false;
        }
        usedLabels.push(filter.label.trim());
        return true;
      });
      return filters.filter((el) => (!module || el.module === module) && !el.isSuggested).slice(-len);
    },
    commonShortList: (module, len = 7) => {
      return this.ui.filters.filter((el) => (!module || el.module === module) && !el.isSuggested).slice(-len);
    },
    suggestedList: (module, len = 7) => {
      return this.ui.filters.filter((el) => (!module || el.module === module) && el.isSuggested).slice(-len);
    },
  };
  bookmarks = {
    hasBookmark(item, bookmark) {
      return bookmark.itemId == item.id && bookmark.category == item.category;
    },
    exists: (item) => {
      return this.ui.bookmarks.items.some((bookmark) => {
        return this.bookmarks.hasBookmark(item, bookmark);
      });
    },
    findBookmark: (item) => {
      return this.ui.bookmarks.items.find((bookmark) => {
        return this.bookmarks.hasBookmark(item, bookmark);
      });
    },
    amount: () => this.ui.bookmarks.total,
    shortlist: (len = 7) => this.ui.bookmarks.items?.slice(-len).reverse(),
    toggle: (item) => {
      const existingBookmark = this.bookmarks.findBookmark(item);
      if (existingBookmark) {
        return this.bookmarks.remove(existingBookmark);
      } else {
        return this.bookmarks.add(item);
      }
    },
    add: async (item) => {
      const { id, category } = item;
      const categoryField = CATEGORY_PLURAL_TO_SINGLE[category];

      const { data } = await this.DM.createBookmarks({
        [categoryField]: id,
      });

      this.ui.bookmarks.items.push(data);
      this.ui.bookmarks.total += 1;
    },
    remove: async (item) => {
      await this.DM.deleteBookmark(item.id).then(() => {
        this.ui.bookmarks.total -= 1;
      });
      this.ui.bookmarks.items = this.ui.bookmarks.items.filter(({ id }) => id !== item.id);
    },
    // Both the profile bootstrap and the bookmarks page need the whole list: whoever
    // asks first pays for it, everyone after reuses the same request.
    ensure: () => {
      if (!this.#bookmarksRequest) {
        this.#bookmarksRequest = this.bookmarks.reload().catch((error) => {
          this.#bookmarksRequest = null;
          throw error;
        });
      }
      return this.#bookmarksRequest;
    },
    reload: async () => {
      const first = await this.loadBookmarks({ itemsPerPage: BOOKMARKS_PAGE_SIZE, page: 1 });
      const restPages = Math.ceil(first.total / BOOKMARKS_PAGE_SIZE) - 1;
      const rest = await Promise.all(
        Array.from({ length: Math.max(restPages, 0) }, (_, index) =>
          this.loadBookmarks({ itemsPerPage: BOOKMARKS_PAGE_SIZE, page: index + 2 })
        )
      );

      this.ui.bookmarks = {
        items: rest.reduce((items, page) => items.concat(page.items), first.items),
        total: first.total,
      };

      return this.ui.bookmarks;
    },
  };

  hasAccess = (category) => {
    const user_cats = [CAT_TRADEMARKS];

    if (!category || user_cats.includes(category)) {
      return true;
    }
    const cat = category?.toLowerCase();
    return RBAC.has(cat) || RBAC.has(`role_${cat}`);
  };

  get isAdmin() {
    return RBAC.has(ROLE_STAFF);
  }
}

UserModel.prototype.addEventListener = function (event, handler) {
  if (!EVENTS.has(event)) {
    EVENTS.set(event, []);
  }
  EVENTS.get(event).push(handler);
  return this;
};

UserModel.prototype.removeEventListener = function (event, handler) {
  const handlers = EVENTS.get(event);
  if (handlers?.length) {
    let index = handlers.indexOf(handler);
    while (index >= 0) {
      handlers.splice(index, 1);
      index = handlers.indexOf(handler);
    }
  }
  return this;
};

export default UserModel;
